import React, { useContext, useState } from "react"
import { AdminContext } from "@/contexts/AdminContext"
import SettingsPricingTable from "./SettingsPricingTable"
import AddEditPriceModal from "../AddEditPriceModal"
import SkeletonTable from "~/components/Admin/Settings/PricesForm/components/skeletons/SettingsPricingTableSkeleton"
import usePricingTable from "~/components/Admin/Settings/PricesForm/hooks/usePricingTable"
import { validDurations, validElectric } from "~/types"
import { SiteProps } from "~/interfaces"
import PricingMatrixConfirmationModal from "~/components/Admin/Settings/PricesForm/components/PricingMatrixConfirmationModal"
import { isCampingStyleIdInValues } from "~/components/Admin/Settings/PricesForm/utils"
import usePricingTableForm from "./usePricingTableForm"
import SaveButtons from "../../../SaveButtons"

const PricingTable = () => {
  const { reactHookForm, sites: allSites, campingStyle, showNotification } = usePricingTableForm()
  const {
    isLoading,
    sites,
    headers,
    priceConfig,
    sitePriceCounts,
    enablePrice,
    disablePrice,
    selectAll,
    unselectAll,
    updatePriceConfig,
    removePriceConfig,
  } = usePricingTable({
    campingStyle,
    reactHookForm,
    allSites,
    showNotification,
  })

  const [modalState, setModalState] = useState<{
    open: boolean
    isEditMode: boolean
    priceKey?: string
    initialPriceData?: {
      amount_in_cents: number
      duration: validDurations
      electric: validElectric
    }
    isPriceUsed: boolean
  }>({
    open: false,
    isEditMode: false,
    priceKey: undefined,
    initialPriceData: undefined,
    isPriceUsed: false,
  })

  const [confirmationState, setConfirmationState] = useState<{
    open: boolean
    priceKey?: string
    conflictKey?: string
    conflictingSites?: SiteProps[]
    newPriceData?: {
      amount_in_cents: number
      duration: validDurations
      electric: validElectric
    }
  }>({
    open: false,
    priceKey: undefined,
    conflictingSites: undefined,
    newPriceData: undefined,
  })

  if (!allSites) return

  const onNewPrice = () =>
    setModalState({
      open: true,
      isEditMode: false,
      priceKey: undefined,
      initialPriceData: undefined,
      isPriceUsed: false,
    })

  const onEditPrice = (
    priceKey: string,
    amount_in_cents: number,
    duration: validDurations,
    electric: validElectric,
  ) => {
    setModalState({
      open: true,
      isEditMode: true,
      priceKey,
      initialPriceData: { amount_in_cents, duration, electric },
      isPriceUsed: priceConfig[priceKey].some(({ _destroy }) => !_destroy),
    })
  }

  const onCloseModal = () => {
    setModalState({
      open: false,
      isEditMode: false,
      priceKey: undefined,
      initialPriceData: undefined,
      isPriceUsed: false,
    })
  }

  const onSubmitPrice = (priceData: { amount_in_cents: number; duration: validDurations; electric: validElectric }) => {
    const newKey = `${priceData.duration}_${priceData.electric}_${priceData.amount_in_cents}`

    const configExists = isCampingStyleIdInValues(priceConfig, newKey)

    const oldKey = modalState.isEditMode && modalState.priceKey ? modalState.priceKey : undefined

    const keyChanged = oldKey && newKey !== oldKey

    if (configExists.isInPriceConfig && (!oldKey || keyChanged)) {
      const oldSites = oldKey ? priceConfig[oldKey] || [] : []
      const newSites = priceConfig[newKey] || []
      const mergedSiteIds = Array.from(new Set([...newSites, ...oldSites]))
      const conflictingSites = sites.filter((site) => mergedSiteIds.find((mergedSite) => site.id === mergedSite.siteId))

      setConfirmationState({
        open: true,
        priceKey: oldKey,
        conflictKey: configExists.oldConfigKey,
        conflictingSites,
        newPriceData: priceData,
      })
    } else {
      updatePriceConfig(oldKey, priceData)
      onCloseModal()
    }
  }

  const onRemovePrice = () => {
    if (modalState.priceKey) {
      removePriceConfig(modalState.priceKey)
      onCloseModal()
    }
  }

  const onConfirmUpdate = () => {
    if (confirmationState.priceKey && confirmationState.newPriceData) {
      updatePriceConfig(confirmationState.priceKey, confirmationState.newPriceData, true, confirmationState.conflictKey)
      setConfirmationState({
        open: false,
        priceKey: undefined,
        conflictKey: undefined,
        conflictingSites: undefined,
        newPriceData: undefined,
      })
      onCloseModal()
    }
  }

  const onCancelUpdate = () => {
    setConfirmationState({
      open: false,
      priceKey: undefined,
      conflictKey: undefined,
      conflictingSites: undefined,
      newPriceData: undefined,
    })
  }

  return (
    <form>
      <SaveButtons
        isDirty={reactHookForm.formState?.isDirty}
        errors={reactHookForm.formState?.errors}
        handleCancel={() => {}}
        isLoading={false}
      />
      {(isLoading && <SkeletonTable />) || (
        <>
          <SettingsPricingTable
            priceConfig={priceConfig}
            onNewPrice={onNewPrice}
            onEditPrice={onEditPrice}
            sites={sites}
            headers={headers}
            enablePrice={enablePrice}
            disablePrice={disablePrice}
            selectAll={selectAll}
            unselectAll={unselectAll}
            sitePriceCounts={sitePriceCounts}
          />
          {modalState.open && (
            <AddEditPriceModal
              open={modalState.open}
              onClose={onCloseModal}
              onSubmit={onSubmitPrice}
              onRemove={onRemovePrice}
              isEditMode={modalState.isEditMode}
              initialPriceData={modalState.initialPriceData}
              isPriceUsed={modalState.isPriceUsed}
            />
          )}
          {confirmationState.open && (
            <PricingMatrixConfirmationModal
              open={confirmationState.open}
              newPriceData={confirmationState.newPriceData}
              conflictingSites={confirmationState.conflictingSites}
              onCancelUpdate={onCancelUpdate}
              onConfirmUpdate={onConfirmUpdate}
            />
          )}
        </>
      )}
    </form>
  )
}

export default PricingTable
